import React, { useMemo, useRef, useState } from 'react';
import type { AlertType } from '@/common/alertType';
import { ALERT_TYPE_LABELS } from '@/common/alertType';
import { Checkbox, Divider, Flex, Select, Tag, Typography } from 'antd';
import { CloseOutlined, SearchOutlined } from '@ant-design/icons';
import { useFetchAlertThresholds } from '@/hooks';
import { CustomShipmentText, TruncateWithTooltip } from '@/components';
import styles from './DropdownVariablePicker.module.css';
import panelStyles from './VariablePickerPanel.module.css';
import { CustomButton } from '@/components';
import { CustomSelectIcon, WarningIcon } from '@/assets/icons/icons';
import { AlertPhaseOption } from '@/apis/logiblocksAPI';

const { Text } = Typography;

export interface AlertTypeValuePickerProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
  rhsOptions?: AlertPhaseOption[];
}

// interface AlertOption extends FieldOption {
//   description?: string;
//   sensor_required?: "required" | "optional" | "none";
//   is_critical_type?: boolean;
//   is_enabled?: boolean;
// }

// interface AlertTypeOptions{
//   phase:string;
//   phase_display:string;
//   alerts:AlertOption[]
// }
export interface AlertTypePickerPanelProps {
  value: string;
  onChange: (value: string) => void;
  onClose?: () => void;
  disabled?: boolean;
  alertTypes: AlertPhaseOption[];
}

/**
 * The scrollable panel content for AlertTypeValuePicker. Exported separately
 * so it can be embedded inside other dropdowns (e.g. DropdownVariablePicker).
 */
export function AlertTypePickerPanel({
  value,
  onChange,
  onClose,
  disabled = false,
  alertTypes,
}: AlertTypePickerPanelProps) {
  const [query, setQuery] = useState('');
  const [pendingValue, setPendingValue] = useState<string>(value ?? '');

  const filteredGroups = useMemo(() => {
    if (!query) return alertTypes;
    const q = query.toLowerCase();
    return alertTypes
      .map((group) => ({
        ...group,
        alerts: group.alerts.filter(
          (a) =>
            a.label.toLowerCase().includes(q) ||
            (a.description ?? '').toLowerCase().includes(q)
        ),
      }))
      .filter((group) => group.alerts.length > 0);
  }, [alertTypes, query]);

  const handleFooterAdd = () => {
    if (pendingValue) onChange(pendingValue);
    onClose?.();
  };

  const handleFooterCancel = () => {
    setPendingValue(value ?? '');
    onClose?.();
  };

  return (
    <div className={styles.alertPickerPanel}>
      {/* Header - Fixed */}
      <div className={styles.alertPickerHeader}>
        <div className={panelStyles.search} style={{ marginBottom: 12 }}>
          <SearchOutlined className={panelStyles.searchIcon} />
          <input
            placeholder='Search...'
            type='text'
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className={panelStyles.searchInput}
            autoFocus
          />
        </div>
        <Flex gap={8} align='center' className={styles.groupHeader}>
          <div
            className={'lb-icon-wrapper'}
            style={{
              backgroundColor: 'var(--info-thin)',
            }}
          >
            <WarningIcon fill={'#266EF1'} width={14} height={14} />
          </div>
          <span className='font-12 fw-600'>{'Alert Trigger'}</span>
        </Flex>
        <hr className={panelStyles.divider} style={{ margin: '16px 0' }} />
        <CustomShipmentText text='Alert type' fontWeight={600} />
        <hr className={panelStyles.divider} style={{ margin: '16px 0' }} />
      </div>

      {/* Scrollable Content */}
      <div className={styles.alertPickerContent}>
        {filteredGroups?.length > 0 ? (
          filteredGroups?.map((group, index) => (
            <React.Fragment key={group?.phase_display || index}>
              {index !== 0 && (
                <Divider
                  style={{ borderColor: 'var(--gray-300)', margin: '16px 0' }}
                />
              )}
              <CustomShipmentText
                text={group?.phase_display}
                fontSize='14px'
                fontWeight='600'
              />
              <Flex align='flex-start'>
                <Flex vertical>
                  {group?.alerts?.map((rule) => {
                    const isSelected = rule?.value === pendingValue;
                    return (
                      <div
                        key={rule?.value}
                        onClick={() => !disabled && setPendingValue(rule.value)}
                        style={{
                          backgroundColor: isSelected
                            ? 'var(--surface-selected)'
                            : 'transparent',
                          borderRadius: '8px',
                          cursor: disabled ? 'not-allowed' : 'pointer',
                          padding: '12px 8px',
                        }}
                      >
                        <Flex align='center' gap={16}>
                          <Checkbox
                            checked={isSelected}
                            onChange={() =>
                              !disabled && setPendingValue(rule.value)
                            }
                            onClick={(e) => e.stopPropagation()}
                            disabled={disabled}
                          />
                          <Flex vertical gap={4}>
                            <Text className='font-14 lh-16 fw-600'>
                              {rule?.label}
                            </Text>
                            <CustomShipmentText
                              text={rule.description as string}
                              lineHeight='20px'
                              color={'var(--text-secondary)'}
                            />
                          </Flex>
                        </Flex>
                      </div>
                    );
                  })}
                </Flex>
              </Flex>
            </React.Fragment>
          ))
        ) : (
          <Flex justify='center' style={{ padding: '16px 0' }}>
            <CustomShipmentText
              text='No alert types available'
              fontWeight={500}
            />
          </Flex>
        )}
      </div>

      {/* Footer - Fixed */}
      <div className={styles.alertPickerFooter}>
        <Divider style={{ borderColor: 'var(--gray-300)', margin: '8px 0' }} />
        <Flex justify='flex-end' gap={8} style={{ paddingTop: 4 }}>
          <CustomButton
            text={'Cancel'}
            bgColor='transparent'
            color='var(--text-primary)'
            fontSize='14px'
            fontWeight='600'
            boxShadow='none'
            height='36px'
            onClick={handleFooterCancel}
          />
          <CustomButton
            text={'Add'}
            bgColor='#000'
            color='#fff'
            fontSize='14px'
            fontWeight='600'
            height={'36px'}
            borderRadius='8px'
            padding='10 12px'
            onClick={handleFooterAdd}
            width='60px'
          />
        </Flex>
      </div>
    </div>
  );
}

/**
 * Dropdown picker for selecting a single alert type value.
 * Opens a grouped list panel (AlertTypePickerPanel); the selected value is
 * displayed as a removable Tag.
 */
export function AlertTypeValuePicker({
  value,
  onChange,
  disabled = false,
  style,
  rhsOptions = [],
}: AlertTypeValuePickerProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const { alertThreshold } = useFetchAlertThresholds(null);

  // Resolve the human-readable label for the current value.
  const selectedLabel = (() => {
    if (!value) return null;
    for (const group of alertThreshold ?? []) {
      const found = group.alerts?.find((a) => a.alert_type === value);
      if (found) return found.alert_name;
    }
    return ALERT_TYPE_LABELS[value as AlertType] ?? value;
  })();

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled) {
      onChange('');
    }
  };

  const dropdownContent = (
    <div className={styles.alertPickerPanelWrapper}>
      <AlertTypePickerPanel
        value={value}
        alertTypes={rhsOptions}
        onChange={(s) => {
          onChange(s);
        }}
        onClose={() => setOpen(false)}
        disabled={disabled}
      />
    </div>
  );

  return (
    <div ref={triggerRef} data-testid='dropdown-trigger'>
      <Select
        open={open && !disabled}
        onOpenChange={(v) => !disabled && setOpen(v)}
        disabled={disabled}
        popupRender={() => dropdownContent}
        className='lb-select'
        popupMatchSelectWidth={true}
        suffixIcon={<CustomSelectIcon />}
        placeholder='Select alert type...'
        value={selectedLabel}
        styles={{
          popup: {
            root: {
              padding: 0,
            },
          },
        }}
        labelRender={() =>
          selectedLabel ? (
            <Tag
              closeIcon={
                <CloseOutlined
                  style={{ fontSize: 10, color: 'var(--text-primary)' }}
                />
              }
              className={styles.selectedTag}
              onClose={handleClear}
            >
              <TruncateWithTooltip
                text={String(selectedLabel)}
                max={25}
                render={(t) => (
                  <span className={styles.selectedTagLabel}>{t}</span>
                )}
              />
            </Tag>
          ) : (
            <span style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
              Select alert type...
            </span>
          )
        }
        style={{
          width: '100%',
          minHeight: 36,
          ...style,
        }}
      />
    </div>
  );
}
